/**
 * The mapping between absolute media seconds and the scrub bar's own 0–100 coordinates.
 *
 * The bar never spans the media — it spans the *clip*, whose bounds come from `clipRange`. In
 * `moment` mode that clip is the selected range, so the same second maps to a different
 * percentage than it does in `full` mode. Two readers need this mapping and they must not
 * disagree: the scene ticks (`ChapterMark`) and the selection band. Deriving it twice is how
 * a tick and a band drawn from the same second end up a few pixels apart.
 *
 * Pure and dependency-free so it can be unit-tested without a DOM — see `__tests__`.
 */

/** The span the bar represents, in absolute media seconds. From `clipRange` in `VideoPlayer`. */
export interface ClipSpan {
  start: number;
  end: number;
}

/**
 * Whether this span can carry a mapping at all. A zero or negative span divides by zero, which
 * is reachable: `media.metadata?.video?.duration` is `0` until metadata lands, and a moment
 * whose bounds have not been seeded yet collapses to a point.
 */
export const hasSpan = (clip: ClipSpan): boolean => clip.end - clip.start > 0;

/**
 * Absolute seconds → percentage of the bar.
 *
 * Deliberately NOT clamped to 0–100. A scene that starts before the clip and ends inside it has
 * to keep its negative `startPercent`, or the hover lookup (`startPercent <= p < endPercent`)
 * stops resolving it — and the bar clips the drawn tick itself via `overflow: hidden`. Callers
 * that need a clamped value are the ones positioning something draggable; they clamp there.
 */
export const percentOfClip = (seconds: number, clip: ClipSpan): number =>
  ((seconds - clip.start) / (clip.end - clip.start)) * 100;

/**
 * Percentage of the bar → absolute seconds, clamped to the clip.
 *
 * Clamped where `percentOfClip` is not, because this direction serves pointer input: a drag
 * that leaves the bar reports a percentage past either end, and a selection bound outside the
 * clip is not a thing the player can play.
 */
export const secondsAtPercent = (percent: number, clip: ClipSpan): number => {
  const clamped = Math.max(0, Math.min(100, percent));
  return clip.start + (clamped / 100) * (clip.end - clip.start);
};
